import type { wave } from '@round/api';
import { getInviteOffer, PLATFORM_ICON, PLATFORM_LABEL } from '@/utility/campaignDisplay';
import PlatformIcon from '@/creator/components/PlatformIcon';
import AudioLinks from '@/creator/components/AudioLinks';
import CpmRateNote from '@/creator/components/CpmRateNote';
import { countOffers, getOfferSummary, getSharedPayoutUnlocks } from '@/utility/contracts';

const getPostingLimit = (maxPosts: number | null): string => {
    if (maxPosts === null) {
        return 'Unlimited';
    }
    return `${maxPosts} ${maxPosts === 1 ? 'post' : 'posts'}`;
};

type Props = {
    invite: wave.Invite;
    contracts: wave.CreatorContract[];
};

const CampaignOverview = ({ invite, contracts }: Props) => {
    const { amount: offerAmount, detail: offerDetail } = getInviteOffer(invite);
    const offerCount = countOffers(contracts);
    const hasSeveralOffers = offerCount > 1;
    const offerSummary = getOfferSummary(contracts);
    const fallbackPayoutUnlocks = invite.campaign_type === 'cpm' ? 'As approved views grow' : 'After post approval';
    // With offers that pay out differently, each offer card says when it pays instead.
    const payoutUnlocks = contracts.length > 0 ? getSharedPayoutUnlocks(contracts) : fallbackPayoutUnlocks;
    const platforms = [...new Set(invite.allowed_accounts.map((account) => account.platform))];
    const accountValue =
        invite.allowed_accounts.length === 1
            ? invite.allowed_accounts[0].handle
            : `${invite.allowed_accounts.length} accounts`;
    const platformLabel = platforms.map((platform) => PLATFORM_LABEL[platform]).join(' and ');

    return (
        <section className="campaign-overview" aria-label="Campaign overview">
            {hasSeveralOffers ? (
                <div className="campaign-overview__item campaign-overview__item--offer">
                    <span>Your offers</span>
                    <strong>{offerCount === 1 ? '1 offer' : `${offerCount} offers`}</strong>
                    <small>Each has its own terms.</small>
                </div>
            ) : offerSummary ? (
                <div className="campaign-overview__item campaign-overview__item--offer">
                    <span>{offerSummary.label}</span>
                    <strong>{offerSummary.amount}</strong>
                    <small>{offerSummary.detail}</small>
                    {invite.campaign_type === 'cpm' ? <CpmRateNote /> : null}
                </div>
            ) : (
                <div className="campaign-overview__item campaign-overview__item--offer">
                    <span>{invite.campaign_type === 'cpm' ? 'Maximum earnable' : 'Per-post offer'}</span>
                    <strong>{offerAmount}</strong>
                    <small>{offerDetail}</small>
                    {invite.campaign_type === 'cpm' ? <CpmRateNote /> : null}
                </div>
            )}
            <div className="campaign-overview__item">
                <span>Accounts invited</span>
                <strong className="campaign-overview__accounts" aria-label={`${platformLabel}, ${accountValue}`}>
                    <span className="campaign-overview__platforms" aria-hidden="true">
                        {platforms.map((platform) => (
                            <span
                                className={`platform-logo platform-logo--${PLATFORM_ICON[platform].toLowerCase()}`}
                                key={platform}
                            >
                                <PlatformIcon platform={PLATFORM_ICON[platform]} size={15} />
                            </span>
                        ))}
                    </span>
                    <span>{accountValue}</span>
                </strong>
            </div>
            {hasSeveralOffers ? null : (
                <div className="campaign-overview__item">
                    <span>Posting limit</span>
                    <strong>{offerSummary?.postingLimit ?? getPostingLimit(invite.effective_max_posts)}</strong>
                </div>
            )}
            {payoutUnlocks === null ? null : (
                <div className="campaign-overview__item">
                    <span>Payout unlocks</span>
                    <strong>{payoutUnlocks}</strong>
                </div>
            )}
            {invite.audios.length > 0 ? (
                <div className="campaign-overview__item campaign-overview__item--audios">
                    <span>{invite.audios.length === 1 ? 'Audio' : 'Audios'}</span>
                    <AudioLinks audios={invite.audios} />
                </div>
            ) : null}
        </section>
    );
};

export default CampaignOverview;
